/**
 * Guided meditation: a short session the member is walked through, one step at
 * a time, on a timer.
 *
 * Built in as CODE with keyed overrides, like the breathwork patterns and the
 * mantras. The prototype either picked one from an admin library or had Claude
 * invent one per request; a written set costs nothing per session, reads the
 * same every time, and cannot drift into a claim on a bad generation. A tenant
 * replaces or hides one with a `coaching_content` row of kind `meditation`.
 *
 * `COACHING_MEDITATION_DISCLAIMER` goes on every screen that shows one: this is
 * a reflective practice, not therapy and not treatment.
 */

export const COACHING_MEDITATION_INTENTIONS = [
  'calm',
  'focus',
  'abundance',
  'confidence',
  'gratitude',
  'sleep',
] as const;
export type CoachingMeditationIntention = (typeof COACHING_MEDITATION_INTENTIONS)[number];

export const COACHING_MEDITATION_INTENTION_LABELS: Record<CoachingMeditationIntention, string> = {
  calm: 'Calm',
  focus: 'Focus',
  abundance: 'Abundance',
  confidence: 'Confidence',
  gratitude: 'Gratitude',
  sleep: 'Sleep',
};

export const COACHING_MEDITATION_DISCLAIMER =
  'Meditation here is a reflective practice for rest and attention. It is not therapy, not medical treatment, and not a substitute for either.';

/** Bounds a tenant-written session must stay inside. */
export const MEDITATION_LIMITS = { stepsMax: 12, stepSecondsMax: 600, minutesMax: 45 } as const;

export type MeditationStep = {
  /** What the member is asked to do, in the second person. */
  text: string;
  seconds: number;
};

export const BUILT_IN_MEDITATION_KEYS = [
  'settle',
  'body-scan',
  'one-breath-focus',
  'receiving',
  'rehearse',
  'gratitude-three',
  'wind-down',
] as const;
export type BuiltInMeditationKey = (typeof BUILT_IN_MEDITATION_KEYS)[number];

export type BuiltInMeditation = {
  key: BuiltInMeditationKey;
  title: string;
  description: string;
  intention: CoachingMeditationIntention;
  steps: MeditationStep[];
};

/** Whole minutes, rounded up, for the card. */
export function meditationMinutes(steps: readonly MeditationStep[]): number {
  return Math.max(1, Math.ceil(steps.reduce((s, step) => s + step.seconds, 0) / 60));
}

export const BUILT_IN_MEDITATIONS: readonly BuiltInMeditation[] = [
  {
    key: 'settle',
    title: 'Settling',
    description:
      'Three minutes to arrive. The shortest session here, and the one to use when you have talked yourself out of sitting at all.',
    intention: 'calm',
    steps: [
      {
        text: 'Sit however you are sitting. Let your shoulders drop and close your eyes.',
        seconds: 20,
      },
      {
        text: 'Breathe out longer than you breathe in. Do that five or six times, without counting carefully.',
        seconds: 60,
      },
      {
        text: 'Notice where your body is touching the chair or the floor. Rest your attention there.',
        seconds: 60,
      },
      { text: 'Let the breath go back to normal. Sit with it a moment longer.', seconds: 40 },
    ],
  },
  {
    key: 'body-scan',
    title: 'Body scan',
    description:
      'Move your attention slowly from head to feet. Good after a day spent entirely in your head.',
    intention: 'calm',
    steps: [
      { text: 'Close your eyes and take three slow breaths.', seconds: 30 },
      {
        text: 'Bring your attention to your face. Let your jaw and the space between your eyebrows soften.',
        seconds: 60,
      },
      {
        text: 'Move down to your shoulders, arms and hands. Notice anything you are holding, and let it go if it will go.',
        seconds: 75,
      },
      {
        text: 'Move to your chest and stomach. Let the breath move them without helping it.',
        seconds: 75,
      },
      { text: 'Move down your legs to your feet. Feel the weight of them.', seconds: 75 },
      {
        text: 'Take in the whole body at once, then open your eyes when you are ready.',
        seconds: 45,
      },
    ],
  },
  {
    key: 'one-breath-focus',
    title: 'One breath at a time',
    description:
      'Counting practice for a scattered mind. Every time you lose the count you start again, which is the whole exercise.',
    intention: 'focus',
    steps: [
      {
        text: 'Sit upright. Let the breath be as it is; you are only going to count it.',
        seconds: 25,
      },
      {
        text: 'Count each out-breath, one to ten. When you reach ten, start again at one.',
        seconds: 180,
      },
      {
        text: 'When you notice you have lost the count, that noticing is the practice. Start again at one, without comment.',
        seconds: 150,
      },
      { text: 'Stop counting. Sit for a few breaths with nothing to do.', seconds: 45 },
    ],
  },
  {
    key: 'receiving',
    title: 'Practising receiving',
    description:
      'Abundance work for people who are better at chasing than at accepting. Nothing is asked for here.',
    intention: 'abundance',
    steps: [
      { text: 'Settle and take three slow breaths.', seconds: 30 },
      {
        text: 'Bring to mind one thing you already have that you once wanted. Stay with it long enough to feel ordinary about it.',
        seconds: 90,
      },
      {
        text: 'Notice the urge to move straight on to the next thing. Let the urge be there, and stay where you are.',
        seconds: 75,
      },
      { text: 'Say inwardly: this is enough for now. Repeat it slowly a few times.', seconds: 75 },
      {
        text: 'Come back to the room, keeping the unhurried feeling if it will come with you.',
        seconds: 40,
      },
    ],
  },
  {
    key: 'rehearse',
    title: 'Rehearsing it well',
    description:
      'Walk through something coming up, the way you would like to handle it. A preparation, not a prediction.',
    intention: 'confidence',
    steps: [
      {
        text: 'Settle. Pick one thing coming up in the next few days that you are tight about.',
        seconds: 45,
      },
      {
        text: 'See the beginning of it: where you are, who is there, the first thing you say.',
        seconds: 75,
      },
      {
        text: 'Watch yourself handle the hardest moment of it well. Not perfectly. Well.',
        seconds: 90,
      },
      {
        text: 'Notice how you would like to feel walking away from it, and let that feeling be here now.',
        seconds: 75,
      },
      {
        text: 'Let the scene go. What stays is how you want to carry yourself, not how it has to turn out.',
        seconds: 45,
      },
    ],
  },
  {
    key: 'gratitude-three',
    title: 'Three things',
    description:
      'Three specific things, held one at a time. Specific is the whole trick: a category does not land.',
    intention: 'gratitude',
    steps: [
      { text: 'Settle and take a few slow breaths.', seconds: 30 },
      {
        text: 'Bring one specific thing from today to mind. Not a category: one moment, with detail.',
        seconds: 70,
      },
      { text: 'Let it go and bring a second, from a different part of the day.', seconds: 70 },
      { text: 'A third, this one about a person. Picture them.', seconds: 70 },
      { text: 'Hold all three loosely for a moment, then let them go.', seconds: 40 },
    ],
  },
  {
    key: 'wind-down',
    title: 'Winding down',
    description:
      'For the end of the day, sitting or lying down. It is fine to fall asleep before the end.',
    intention: 'sleep',
    steps: [
      { text: 'Lie down or sit back. Let your eyes close.', seconds: 30 },
      {
        text: 'Breathe out slowly, longer than you breathe in, and let each out-breath be a little heavier.',
        seconds: 90,
      },
      {
        text: 'Run back through the day quickly, without judging any of it. Just let it go past.',
        seconds: 90,
      },
      { text: 'Pick one small thing that went well and rest on it.', seconds: 75 },
      { text: 'Stop doing anything. Let the breath carry on without you.', seconds: 75 },
    ],
  },
];

export function isBuiltInMeditationKey(key: string | null): key is BuiltInMeditationKey {
  return key !== null && (BUILT_IN_MEDITATION_KEYS as readonly string[]).includes(key);
}

export type MeditationPosition = {
  /** Which step the member is on, 0-based. Clamped to the last step at the end. */
  index: number;
  /** Milliseconds spent inside the current step. */
  stepElapsedMs: number;
  totalMs: number;
  elapsedMs: number;
  done: boolean;
};

/** Total milliseconds a session runs for. */
export function meditationTotalMs(steps: readonly MeditationStep[]): number {
  return steps.reduce((sum, step) => sum + step.seconds, 0) * 1000;
}

/**
 * Where `elapsedMs` puts the member in a session.
 *
 * A pure function of ELAPSED time, for the same reason `breathPositionAt` is:
 * a timer that counts its own ticks drifts whenever the browser throttles a
 * background tab or the phone locks, and the member ends up on a step the
 * clock left behind. The screen stores when the session started and asks this
 * where that puts them, so a late tick simply lands later in the same session.
 */
export function meditationPositionAt(
  steps: readonly MeditationStep[],
  elapsedMs: number,
): MeditationPosition {
  const totalMs = meditationTotalMs(steps);
  const elapsed = Math.max(0, Math.min(elapsedMs, totalMs));
  if (steps.length === 0) {
    return { index: 0, stepElapsedMs: 0, totalMs: 0, elapsedMs: 0, done: true };
  }
  if (elapsed >= totalMs) {
    const last = steps.length - 1;
    return {
      index: last,
      stepElapsedMs: steps[last]!.seconds * 1000,
      totalMs,
      elapsedMs: totalMs,
      done: true,
    };
  }
  let remaining = elapsed;
  for (let i = 0; i < steps.length; i++) {
    const stepMs = steps[i]!.seconds * 1000;
    if (remaining < stepMs) {
      return { index: i, stepElapsedMs: remaining, totalMs, elapsedMs: elapsed, done: false };
    }
    remaining -= stepMs;
  }
  // Unreachable: `elapsed < totalMs` means some step still holds it.
  return { index: 0, stepElapsedMs: 0, totalMs, elapsedMs: elapsed, done: false };
}
